import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';

const theme = createTheme({
    palette: {
        primary: {
            main: '#A82175',
            dark: '#7B1C4A',
            light: '#E8C2D8',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#D4956A',
            dark: '#B8733E',
            light: '#F0C4A8',
            contrastText: '#ffffff',
        },
        background: {
            default: '#F8FAFC',
            paper: '#FFFFFF',
        },
        text: {
            primary: '#0F172A',
            secondary: '#1F2937',
            disabled: '#4B5563',
        },
        divider: '#E2E8F0',
        success: {
            main: '#059669',
            light: '#D1FAE5',
            contrastText: '#ffffff',
        },
        error: {
            main: '#DC2626',
            light: '#FEE2E2',
            contrastText: '#ffffff',
        },
        warning: {
            main: '#D97706',
            light: '#FEF3C7',
            contrastText: '#ffffff',
        },
        info: {
            main: '#0EA5E9',
            light: '#E0F2FE',
            contrastText: '#ffffff',
        },
    },
    typography: {
        fontFamily: 'helvetica-lt-std-roman, Helvetica, Arial, sans-serif',
        h1: {
            fontFamily: 'helvetica-lt-std-bold, Helvetica, Arial, sans-serif',
            fontWeight: 700,
            color: '#0F172A',
            letterSpacing: '-0.025em',
        },
        h2: {
            fontFamily: 'helvetica-lt-std-bold, Helvetica, Arial, sans-serif',
            fontWeight: 700,
            color: '#0F172A',
            letterSpacing: '-0.02em',
        },
        h3: {
            fontFamily: 'helvetica-lt-std-bold, Helvetica, Arial, sans-serif',
            fontWeight: 600,
            color: '#0F172A',
            letterSpacing: '-0.015em',
        },
        h4: {
            fontFamily: 'helvetica-lt-std-bold, Helvetica, Arial, sans-serif',
            fontWeight: 600,
            color: '#0F172A',
        },
        h5: {
            fontFamily: 'helvetica-lt-std-bold, Helvetica, Arial, sans-serif',
            fontWeight: 600,
            color: '#0F172A',
        },
        h6: {
            fontFamily: 'helvetica-lt-std-bold, Helvetica, Arial, sans-serif',
            fontWeight: 600,
            color: '#0F172A',
        },
        subtitle1: { color: '#0F172A', lineHeight: 1.6 },
        subtitle2: { color: '#1F2937', lineHeight: 1.6 },
        body1: { color: '#0F172A', lineHeight: 1.6 },
        body2: { color: '#1F2937', lineHeight: 1.5 },
        button: {
            fontFamily: 'helvetica-lt-std-roman, Helvetica, Arial, sans-serif',
            letterSpacing: '0.01em',
            textTransform: 'none',
            fontWeight: 500,
        },
        caption: {
            color: '#1F2937',
            fontSize: '12px',
        },
    },
    shape: {
        borderRadius: 10,
    },
    shadows: [
        'none',
        '0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)',
        '0 4px 6px -1px rgba(15,23,42,0.07), 0 2px 4px -1px rgba(15,23,42,0.04)',
        '0 10px 15px -3px rgba(15,23,42,0.07), 0 4px 6px -2px rgba(15,23,42,0.04)',
        '0 20px 25px -5px rgba(15,23,42,0.07), 0 10px 10px -5px rgba(15,23,42,0.02)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
        '0 25px 50px -12px rgba(15,23,42,0.18)',
    ],
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '10px',
                    padding: '9px 20px',
                    fontWeight: 500,
                    fontSize: '14px',
                    textTransform: 'none',
                    transition: 'all 0.2s ease',
                    letterSpacing: '0.01em',
                    minHeight: '40px',
                },
                contained: {
                    boxShadow: '0 4px 6px -1px rgba(168,33,117,0.2)',
                    '&:hover': {
                        boxShadow: '0 8px 16px -3px rgba(168,33,117,0.3)',
                        transform: 'translateY(-1px)',
                    },
                    '&:active': {
                        transform: 'translateY(0)',
                        boxShadow: '0 2px 4px rgba(168,33,117,0.2)',
                    },
                    '&.Mui-disabled': {
                        background: '#E2E8F0',
                        color: '#94A3B8',
                        boxShadow: 'none',
                    },
                },
                containedPrimary: {
                    background: 'linear-gradient(135deg, #A82175 0%, #7B1C4A 100%)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #7B1C4A 0%, #5C1538 100%)',
                    },
                },
                outlined: {
                    borderWidth: '1.5px',
                    '&:hover': {
                        borderWidth: '1.5px',
                        backgroundColor: '#FDF2F8',
                    },
                },
                outlinedPrimary: {
                    borderColor: '#E0A7C5',
                    '&:hover': {
                        borderColor: '#A82175',
                        backgroundColor: '#FDF2F8',
                    },
                },
                text: {
                    '&:hover': {
                        backgroundColor: '#F1F5F9',
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: '#FDF6FA',
                        transition: 'all 0.2s ease',
                        '& fieldset': {
                            borderColor: '#F0D6E4',
                            transition: 'border-color 0.2s ease',
                        },
                        '&:hover fieldset': {
                            borderColor: '#E0A7C5',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#A82175',
                            borderWidth: '2px',
                        },
                        '&.Mui-focused': {
                            backgroundColor: '#FFFFFF',
                            boxShadow: '0 0 0 4px rgba(168,33,117,0.10)',
                        },
                        '&.Mui-error fieldset': {
                            borderColor: '#FCA5A5',
                        },
                    },
                    '& .MuiInputBase-input': {
                        color: '#1A1A1A',
                        '&::placeholder': {
                            color: '#B58FA4',
                            opacity: 1,
                        },
                    },
                    '& .MuiFormLabel-root': {
                        color: '#1A1A1A',
                        fontSize: '14px',
                        fontWeight: 600,
                        '&.Mui-focused': {
                            color: '#1A1A1A',
                        },
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px -1px rgba(15,23,42,0.06), 0 2px 4px -1px rgba(15,23,42,0.04)',
                },
                elevation0: {
                    boxShadow: 'none',
                },
                elevation1: {
                    boxShadow: '0 4px 6px -1px rgba(15,23,42,0.06), 0 2px 4px -1px rgba(15,23,42,0.04)',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px -1px rgba(15,23,42,0.06), 0 2px 4px -1px rgba(15,23,42,0.04)',
                    border: '1px solid #F1F5F9',
                    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                    '&:hover': {
                        boxShadow: '0 10px 15px -3px rgba(15,23,42,0.08), 0 4px 6px -2px rgba(15,23,42,0.04)',
                    },
                },
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    border: '2px solid #F0D6E8',
                    fontFamily: 'helvetica-lt-std-bold, Helvetica, Arial, sans-serif',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #A72C68 0%, #7B1C4A 100%)',
                    color: '#FFFFFF',
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: '#1E293B',
                    fontSize: '12px',
                    borderRadius: '8px',
                    padding: '6px 12px',
                    boxShadow: '0 4px 6px -1px rgba(15,23,42,0.15)',
                },
                arrow: {
                    color: '#1E293B',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    fontWeight: 500,
                    fontSize: '12px',
                    height: '28px',
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: '10px',
                    marginBottom: '4px',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                        backgroundColor: '#FDF2F8',
                    },
                    '&.Mui-selected': {
                        backgroundColor: '#FDF2F8',
                        color: '#A82175',
                        '&:hover': {
                            backgroundColor: '#FCE9F3',
                        },
                    },
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: '#F1F5F9',
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    '& .MuiTableCell-head': {
                        backgroundColor: '#F8FAFC',
                        color: '#475569',
                        fontWeight: 600,
                        fontSize: '13px',
                        letterSpacing: '0.025em',
                        textTransform: 'uppercase',
                        borderBottom: '1px solid #E2E8F0',
                        padding: '12px 16px',
                    },
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                        backgroundColor: '#F8FAFC',
                    },
                    '& .MuiTableCell-root': {
                        borderColor: '#F1F5F9',
                        padding: '12px 16px',
                        color: '#1E293B',
                        fontSize: '14px',
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: '20px',
                    padding: '8px',
                    boxShadow: '0 25px 50px -12px rgba(15,23,42,0.25)',
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#0F172A',
                    fontFamily: 'helvetica-lt-std-bold, Helvetica, Arial, sans-serif',
                    padding: '20px 24px 8px',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    borderRadius: '10px',
                    backgroundColor: '#FDF6FA',
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#F0D6E4',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E0A7C5',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#A82175',
                        borderWidth: '2px',
                    },
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                option: {
                    color: '#1A1A1A',
                    '&:hover': {
                        backgroundColor: '#A82175 !important',
                        color: '#FFFFFF !important',
                        '& .MuiCheckbox-root': { color: '#FFFFFF !important' },
                    },
                    '&[aria-selected="true"]': {
                        backgroundColor: '#A82175 !important',
                        color: '#FFFFFF !important',
                        '& .MuiCheckbox-root': { color: '#FFFFFF !important' },
                    },
                    '&.Mui-focused': {
                        backgroundColor: '#A82175 !important',
                        color: '#FFFFFF !important',
                        '& .MuiCheckbox-root': { color: '#FFFFFF !important' },
                    },
                    '&[aria-selected="true"].Mui-focused': {
                        backgroundColor: '#A82175 !important',
                        color: '#FFFFFF !important',
                        '& .MuiCheckbox-root': { color: '#FFFFFF !important' },
                    },
                },
                tag: {
                    backgroundColor: '#A82175',
                    color: '#FFFFFF',
                    '& .MuiChip-deleteIcon': {
                        color: '#FFFFFF',
                        '&:hover': { color: '#FCE9F3' },
                    },
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                '::selection': {
                    backgroundColor: '#A82175',
                    color: '#FFFFFF',
                },
                '::-moz-selection': {
                    backgroundColor: '#A82175',
                    color: '#FFFFFF',
                },
            },
        },
        MuiBadge: {
            styleOverrides: {
                badge: {
                    fontWeight: 600,
                    fontSize: '10px',
                    minWidth: '18px',
                    height: '18px',
                    padding: '0 4px',
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: '10px',
                    fontSize: '14px',
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                switchBase: {
                    '&.Mui-checked': {
                        color: '#A82175',
                    },
                    '&.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#E0A7C5',
                    },
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: '#D8B5C7',
                    '&.Mui-checked': {
                        color: '#A82175',
                    },
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    color: '#D8B5C7',
                    '&.Mui-checked': {
                        color: '#A82175',
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    '& .MuiTabs-indicator': {
                        backgroundColor: '#A82175',
                        height: '3px',
                        borderRadius: '3px 3px 0 0',
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#64748B',
                    '&.Mui-selected': {
                        color: '#A82175',
                        fontWeight: 600,
                    },
                },
            },
        },
    },
});

const Theme = ({ children }) => {
    return (
        <ThemeProvider theme={theme}>
            <GlobalStyles
                styles={{
                    '::selection': {
                        backgroundColor: '#A82175',
                        color: '#FFFFFF',
                    },
                    '::-moz-selection': {
                        backgroundColor: '#A82175',
                        color: '#FFFFFF',
                    },
                }}
            />
            {children}
        </ThemeProvider>
    );
};

export default Theme;
